import { Point, Wall } from '../types/room'

export const GRID_SIZE = 10 // 10cm grid
export const WALL_THICKNESS = 20 // 20cm exterior extrusion

// Snap point to grid
export function snapToGrid(point: Point): Point {
  return {
    x: Math.round(point.x / GRID_SIZE) * GRID_SIZE,
    y: Math.round(point.y / GRID_SIZE) * GRID_SIZE,
  }
}

// Calculate distance between two points
export function distance(p1: Point, p2: Point): number {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  return Math.sqrt(dx * dx + dy * dy)
}

// Calculate angle between two points (in degrees)
export function angle(p1: Point, p2: Point): number {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  return (Math.atan2(dy, dx) * 180) / Math.PI
}

// Snap angle to 90/180 degrees
export function snapAngle(angleInDegrees: number): number {
  const normalized = ((angleInDegrees % 360) + 360) % 360
  const snapAngles = [0, 90, 180, 270]
  const threshold = 15 // 15 degree threshold for snapping

  for (const snapAngle of snapAngles) {
    if (Math.abs(normalized - snapAngle) < threshold) {
      return snapAngle
    }
  }

  return normalized
}

// Apply angle snap to a point relative to origin
export function snapPointToAngle(origin: Point, point: Point): Point {
  const dist = distance(origin, point)
  const currentAngle = angle(origin, point)
  const snappedAngle = snapAngle(currentAngle)
  const radians = (snappedAngle * Math.PI) / 180

  return {
    x: origin.x + dist * Math.cos(radians),
    y: origin.y + dist * Math.sin(radians),
  }
}

// Check if point is close to another point (for close loop detection)
export function isNearPoint(
  p1: Point,
  p2: Point,
  threshold: number = 20,
): boolean {
  return distance(p1, p2) < threshold
}

// Check if two points are the same (within tolerance)
export function pointsEqual(
  p1: Point,
  p2: Point,
  tolerance: number = 1,
): boolean {
  return Math.abs(p1.x - p2.x) < tolerance && Math.abs(p1.y - p2.y) < tolerance
}

// Format distance for display (in cm) - PRECISE
export function formatDistance(distanceInCm: number): string {
  // Round to 1 decimal place for precision
  const rounded = Math.round(distanceInCm * 10) / 10
  return `${rounded} cm`
}

// Convert screen coordinates to canvas coordinates
export function screenToCanvas(
  screenX: number,
  screenY: number,
  canvasRect: DOMRect,
  zoom: number = 1,
  pan: Point = { x: 0, y: 0 },
): Point {
  return {
    x: (screenX - canvasRect.left - pan.x) / zoom,
    y: (screenY - canvasRect.top - pan.y) / zoom,
  }
}

// Generate template shapes
export function generateRectangle(start: Point, end: Point): Point[] {
  return [start, { x: end.x, y: start.y }, end, { x: start.x, y: end.y }]
}

// L-Shape: CLOSED (for soffits)
export function generateLShapeClosed(start: Point, end: Point): Point[] {
  const width = end.x - start.x
  const height = end.y - start.y
  const cornerX = start.x + width * 0.6
  const cornerY = start.y + height * 0.6

  return [
    start,
    { x: cornerX, y: start.y },
    { x: cornerX, y: cornerY },
    { x: end.x, y: cornerY },
    { x: end.x, y: end.y },
    { x: start.x, y: end.y },
  ]
}

// U-Shape: CLOSED (for soffits)
export function generateUShapeClosed(start: Point, end: Point): Point[] {
  const width = end.x - start.x
  const height = end.y - start.y
  const leftIndent = start.x + width * 0.25
  const rightIndent = start.x + width * 0.75
  const topIndent = start.y + height * 0.4

  return [
    start,
    { x: end.x, y: start.y },
    { x: end.x, y: end.y },
    { x: rightIndent, y: end.y },
    { x: rightIndent, y: topIndent },
    { x: leftIndent, y: topIndent },
    { x: leftIndent, y: end.y },
    { x: start.x, y: end.y },
  ]
}

// Galley: Two parallel rectangles (for soffits)
export function generateGalleyClosed(start: Point, end: Point): Point[] {
  const width = end.x - start.x
  const height = end.y - start.y
  const gap = height * 0.3
  const barHeight = (height - gap) / 2

  // Create two rectangles
  return [
    // Top rectangle
    start,
    { x: end.x, y: start.y },
    { x: end.x, y: start.y + barHeight },
    { x: start.x, y: start.y + barHeight },
    // Gap (connect to bottom rectangle)
    { x: start.x, y: start.y + barHeight + gap },
    { x: end.x, y: start.y + barHeight + gap },
    { x: end.x, y: end.y },
    { x: start.x, y: end.y },
  ]
}

// Half Trapezoid for soffit (replaces L-Shape)
export function generateHalfTrapezoid(start: Point, end: Point): Point[] {
  const width = Math.abs(end.x - start.x)
  const height = Math.abs(end.y - start.y)

  return [
    start,
    { x: end.x, y: start.y },
    { x: end.x, y: end.y },
    { x: start.x + width * 0.4, y: end.y },
    { x: start.x, y: start.y + height * 0.6 },
  ]
}

// Triangle for soffit (replaces U-Shape)
export function generateTriangle(start: Point, end: Point): Point[] {
  const centerX = (start.x + end.x) / 2

  return [
    { x: start.x, y: end.y },
    { x: centerX, y: start.y },
    { x: end.x, y: end.y },
  ]
}

// L-Shape: OPEN (2 walls only) - for walls
export function generateLShape(start: Point, end: Point): Point[] {
  const width = end.x - start.x
  const height = end.y - start.y
  const cornerX = start.x + width * 0.6
  const cornerY = start.y + height * 0.6

  return [start, { x: cornerX, y: start.y }, { x: cornerX, y: cornerY }]
}

// U-Shape: OPEN (3 walls) - for walls
export function generateUShape(start: Point, end: Point): Point[] {
  const width = end.x - start.x
  const height = end.y - start.y
  const leftIndent = start.x + width * 0.25
  const rightIndent = start.x + width * 0.75
  const topIndent = start.y + height * 0.4

  return [
    start,
    { x: start.x, y: end.y },
    { x: leftIndent, y: end.y },
    { x: leftIndent, y: topIndent },
  ]
}

// Galley: OPEN (2 parallel walls) - for walls
export function generateGalley(start: Point, end: Point): Point[] {
  const width = end.x - start.x
  const wallWidth = width * 0.15

  return [start, { x: start.x, y: end.y }]
}

// Calculate distance from point to wall segment
export function distanceToWall(
  point: Point,
  wallStart: Point,
  wallEnd: Point,
): number {
  const A = point.x - wallStart.x
  const B = point.y - wallStart.y
  const C = wallEnd.x - wallStart.x
  const D = wallEnd.y - wallStart.y

  const dot = A * C + B * D
  const lenSq = C * C + D * D
  let param = -1

  if (lenSq !== 0) param = dot / lenSq

  let xx, yy

  if (param < 0) {
    xx = wallStart.x
    yy = wallStart.y
  } else if (param > 1) {
    xx = wallEnd.x
    yy = wallEnd.y
  } else {
    xx = wallStart.x + param * C
    yy = wallStart.y + param * D
  }

  const dx = point.x - xx
  const dy = point.y - yy

  return Math.sqrt(dx * dx + dy * dy)
}

// Find closest wall to a point
export function findClosestWall(
  point: Point,
  walls: Wall[],
): { wall: Wall; distance: number; position: number } | null {
  let closest: { wall: Wall; distance: number; position: number } | null = null

  for (const wall of walls) {
    const dist = distanceToWall(point, wall.start, wall.end)

    if (!closest || dist < closest.distance) {
      // Calculate position along wall
      const wallLength = distance(wall.start, wall.end)
      const A = point.x - wall.start.x
      const B = point.y - wall.start.y
      const C = wall.end.x - wall.start.x
      const D = wall.end.y - wall.start.y
      const dot = A * C + B * D
      const lenSq = C * C + D * D
      const param = Math.max(0, Math.min(1, dot / lenSq))
      const position = param * wallLength

      closest = { wall, distance: dist, position }
    }
  }

  return closest
}

// Find all walls connected to a specific point
export function findConnectedWalls(
  point: Point,
  walls: Wall[],
  excludeWallId?: string,
): Wall[] {
  return walls.filter((wall) => {
    if (excludeWallId && wall.id === excludeWallId) return false
    return pointsEqual(wall.start, point) || pointsEqual(wall.end, point)
  })
}

// Calculate point on wall at given position
export function getPointOnWall(
  wall: Wall,
  position: number,
): { point: Point; angle: number } {
  const wallLength = distance(wall.start, wall.end)
  const ratio = Math.max(0, Math.min(1, position / wallLength))

  const point = {
    x: wall.start.x + (wall.end.x - wall.start.x) * ratio,
    y: wall.start.y + (wall.end.y - wall.start.y) * ratio,
  }

  const wallAngle = Math.atan2(
    wall.end.y - wall.start.y,
    wall.end.x - wall.start.x,
  )

  return { point, angle: wallAngle }
}

// Get perpendicular offset point from wall
export function getOffsetPoint(
  point: Point,
  angle: number,
  offset: number,
): Point {
  const perpAngle = angle + Math.PI / 2
  return {
    x: point.x + offset * Math.cos(perpAngle),
    y: point.y + offset * Math.sin(perpAngle),
  }
}

// Snap point to nearest wall
export function snapToNearestWall(
  point: Point,
  walls: Wall[],
  maxDistance: number = 50,
): {
  wallId: string
  position: number
  offset: number
  snappedPoint: Point
} | null {
  const closest = findClosestWall(point, walls)

  if (!closest || closest.distance > maxDistance) {
    return null
  }

  const { point: wallPoint, angle } = getPointOnWall(
    closest.wall,
    closest.position,
  )

  // Calculate offset (signed distance from wall)
  const dx = point.x - wallPoint.x
  const dy = point.y - wallPoint.y
  const perpAngle = angle + Math.PI / 2
  const offset = dx * Math.cos(perpAngle) + dy * Math.sin(perpAngle)

  return {
    wallId: closest.wall.id,
    position: closest.position,
    offset,
    snappedPoint: wallPoint,
  }
}

// Update connected walls when a point moves
export interface WallUpdate {
  id: string
  updates: Partial<Wall>
}

export function getConnectedWallUpdates(
  movedPoint: Point,
  newPoint: Point,
  walls: Wall[],
  excludeWallId?: string,
): WallUpdate[] {
  const connectedWalls = findConnectedWalls(movedPoint, walls, excludeWallId)
  const updates: WallUpdate[] = []

  for (const wall of connectedWalls) {
    if (pointsEqual(wall.start, movedPoint)) {
      updates.push({
        id: wall.id,
        updates: { start: newPoint },
      })
    } else if (pointsEqual(wall.end, movedPoint)) {
      updates.push({
        id: wall.id,
        updates: { end: newPoint },
      })
    }
  }

  return updates
}
